import { CalendarOutlined } from "@ant-design/icons";
import { observer } from "mobx-react-lite";
import React, { useEffect, useMemo, useState } from "react";
import { BsFuncParams } from "src/businessServices";
import { DropdownChildButton } from "src/components/buttons/DropdownChildButton";
import { Form, Modal, Spin } from "antd";
import { t } from "i18next";
import { onError } from "src/common/onError";
import { BulkPlanCreationStore, CreationStatus } from "./BulkPlanCreationStore";
import { BulkPlanFooter } from "./BulkPlanContent/BulkPlanFooter";
import { BulkPlanContent } from "./BulkPlanContent";
import styles from "./BulkPlanCreation.module.less";
import { BulkPlanTempSelector } from "./BulkPlanTempSelector";
import { EdBulkPlanCreation } from "./EdBulkPlanCreation";

export const BulkPlanCreation: React.FC<BsFuncParams> = observer(
  ({ name, info, entities }) => {
    const store = useMemo(() => new BulkPlanCreationStore(), []);
    const { open, creationStatus } = store;
    const [form] = Form.useForm<EdBulkPlanCreation>();
    const [loading, setLoading] = useState(false);

    useEffect(() => {
      store.init(entities, info.id);
    }, [info.id, entities]);

    const handleOpen = () => {
      setLoading(true);
      store
        .checkPlanCreation()
        .finally(() => setLoading(false))
        .catch(onError);
    };

    const titleMap: Record<CreationStatus, string> = {
      blocked: t("Creating a calendar plan is not possible"),
      canCreate: t("Create a calendar plan"),
      decision: t("Continue to create a calendar plan?"),
    };

    return (
      <>
        <DropdownChildButton
          icon={<CalendarOutlined />}
          onClick={handleOpen}
          disabled={!entities?.selected.length}
        >
          {name}
        </DropdownChildButton>
        <Modal
          centered
          open={open}
          title={creationStatus && titleMap[creationStatus]}
          onCancel={() => {
            store.onCLose();
            form.resetFields();
          }}
          footer={<BulkPlanFooter store={store} form={form} />}
          modalRender={(content) => (
            <Form
              form={form}
              name="bulkPlanCreation"
              onFinish={(values) => store.createTempSelector(values)}
              layout="vertical"
              initialValues={{ changeStartDate: false }}
            >
              {content}
            </Form>
          )}
        >
          {loading && (
            <div className={styles.loading}>
              <Spin size="large" spinning />
              <div>{t("Checking whether instances have a calendar plan")}</div>
            </div>
          )}
          <BulkPlanContent store={store} />
        </Modal>
        <BulkPlanTempSelector store={store} />
      </>
    );
  },
);
